/**
 * How a card reads, in glyphs and in words.
 *
 * Suit is always drawn as a glyph rather than as color alone, and the spoken
 * label spells out both suit and color, so nothing a player needs to know is
 * carried by red-versus-black.
 */

import { type Card, type Rank, type Suit, isWild } from "@ryzun/haf-saloon-game-engine";

const SUIT_GLYPHS: Readonly<Record<Suit, string>> = {
    clubs: "♣",
    diamonds: "♦",
    hearts: "♥",
    spades: "♠"
};

const RANK_GLYPHS: Readonly<Record<Rank, string>> = {
    2: "2",
    3: "3",
    4: "4",
    5: "5",
    6: "6",
    7: "7",
    8: "8",
    9: "9",
    10: "10",
    ace: "A",
    jack: "J",
    joker: "★",
    king: "K",
    queen: "Q"
};

const RANK_WORDS: Readonly<Record<Rank, string>> = {
    2: "two",
    3: "three",
    4: "four",
    5: "five",
    6: "six",
    7: "seven",
    8: "eight",
    9: "nine",
    10: "ten",
    ace: "ace",
    jack: "jack",
    joker: "joker",
    king: "king",
    queen: "queen"
};

const PLURAL_RANK_WORDS: Readonly<Record<Rank, string>> = {
    2: "twos",
    3: "threes",
    4: "fours",
    5: "fives",
    6: "sixes",
    7: "sevens",
    8: "eights",
    9: "nines",
    10: "tens",
    ace: "aces",
    jack: "jacks",
    joker: "jokers",
    king: "kings",
    queen: "queens"
};

export function getSuitGlyph(suit: Suit | null): string {
    return suit === null ? "" : SUIT_GLYPHS[suit];
}

export function getRankGlyph(rank: Rank): string {
    return RANK_GLYPHS[rank];
}

export function getRankWord(rank: Rank, plural = false): string {
    return plural ? PLURAL_RANK_WORDS[rank] : RANK_WORDS[rank];
}

/**
 * A card named inside a sentence. The spoken label is deliberately verbose, so
 * this is the shorter form for text on screen: the suit stays a glyph, and a
 * card without one is named by its color instead.
 */
export function getCardPhrase(card: Card): string {
    if (card.suit === null) {
        return `${card.color} ${getRankWord(card.rank)}`;
    }

    return `${getRankWord(card.rank)} ${getSuitGlyph(card.suit)}`;
}

/**
 * The accessible name for a card. Color is spelled out alongside the suit
 * because a four-color deck or a high-contrast theme may change what is drawn,
 * and because "red" and "black" matter to the rules for threes and books (§6).
 */
export function getCardLabel(card: Card): string {
    const wild = isWild(card) ? ", wild" : "";

    if (card.suit === null) {
        return `${card.color} ${getRankWord(card.rank)}${wild}`;
    }

    return `${getRankWord(card.rank)} of ${card.suit}, ${card.color}${wild}`;
}
